import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import QRCode from "qrcode";
import { toPng, toJpeg } from "html-to-image";
import jsPDF from "jspdf";
import Cropper from "react-easy-crop";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { CREDENTIAL_FONTS, loadFont } from "@/lib/credentialFonts";
import {
  Type, Image as ImageIcon, QrCode, User, Trash2, Bold, AlignLeft, AlignCenter, AlignRight,
  Save, RotateCcw, X, Loader2, ChevronRight, Palette, Layers, Square,
  RectangleHorizontal, RectangleVertical, LayoutTemplate, Crop, Check, ZoomIn,
} from "lucide-react";

const CARD_W = 1012; // ancho de referencia (px) para escalar fuentes
const uid = () => Math.random().toString(36).slice(2, 9);
// Tarjeta estándar CR80: 85.6 mm x 54 mm. ratio = ancho/alto.
const ratioFor = (o) => (o === "portrait" ? 54 / 85.6 : 85.6 / 54);

// Paleta base solicitada + colores extra (el selector nativo cubre "todos los colores").
const PALETTE = [
  "#000000", "#ffffff", "#facc15", "#dc2626", "#16a34a", "#f97316", "#2563eb", "#ec4899",
  "#0b1f3a", "#7c3aed", "#0891b2", "#65a30d", "#b91c1c", "#9d174d", "#1e3a8a", "#14532d",
  "#c2410c", "#374151", "#94a3b8", "#f5c518",
];

const DYNAMIC_FIELDS = [
  { token: "{{nombre}}", label: "Nombre del alumno/docente" },
  { token: "{{grado}}", label: "Grado / Materia" },
  { token: "{{uid}}", label: "Matrícula / UID" },
  { token: "{{colegio}}", label: "Nombre del colegio" },
  { token: "{{nivel}}", label: "Nivel" },
  { token: "{{sangre}}", label: "Tipo de sangre" },
];

const QUICK_TEXTS = [
  "CREDENCIAL", "IDENTIFICACIÓN OFICIAL", "CICLO ESCOLAR 2025 - 2026", "ALUMNO", "DOCENTE",
  "TIPO DE SANGRE:", "CONTACTO DE EMERGENCIA:", "Esta credencial es personal e intransferible.",
  "En caso de extravío, favor de reportarla a la institución.", "Válida únicamente con firma.",
  "VIGENCIA:", "MATRÍCULA:", "NIVEL:", "GRUPO:", "www.micolegio.edu.mx",
];

// 10 plantillas ESTRUCTURALMENTE distintas (cada una con su propio layout + colores).
const THEMES = [
  { name: "Marino", layout: "sidebar", bg: "#0b1f3a", accent: "#3b62a8", accent2: "#1e3a8a", text: "#ffffff", sub: "#9db4d8", muted: "#94a3b8", onAccent: "#ffffff" },
  { name: "Negro oro", layout: "topband", bg: "#0f0f10", accent: "#f5c518", accent2: "#d4af37", text: "#ffffff", sub: "#d4af37", muted: "#a1a1aa", onAccent: "#0f0f10" },
  { name: "Blanco", layout: "diagonal", bg: "#ffffff", accent: "#dc2626", accent2: "#f97316", text: "#111827", sub: "#6b7280", muted: "#9ca3af", onAccent: "#ffffff" },
  { name: "Rojo", layout: "bottomband", bg: "#b91c1c", accent: "#7f1d1d", accent2: "#fca5a5", text: "#ffffff", sub: "#fecaca", muted: "#fee2e2", onAccent: "#ffffff" },
  { name: "Verde", layout: "frame", bg: "#14532d", accent: "#4ade80", accent2: "#16a34a", text: "#ffffff", sub: "#bbf7d0", muted: "#86efac", onAccent: "#052e16" },
  { name: "Naranja", layout: "split", bg: "#ffffff", accent: "#c2410c", accent2: "#ea580c", text: "#7c2d12", sub: "#9a3412", muted: "#c2410c", onAccent: "#ffffff" },
  { name: "Azul", layout: "corners", bg: "#1e3a8a", accent: "#60a5fa", accent2: "#93c5fd", text: "#ffffff", sub: "#bfdbfe", muted: "#93c5fd", onAccent: "#0b1f3a" },
  { name: "Rosa", layout: "centered", bg: "#9d174d", accent: "#f9a8d4", accent2: "#f472b6", text: "#ffffff", sub: "#fbcfe8", muted: "#fce7f3", onAccent: "#500724" },
  { name: "Amarillo", layout: "photohero", bg: "#fde68a", accent: "#1f2937", accent2: "#f59e0b", text: "#1f2937", sub: "#374151", muted: "#4b5563", onAccent: "#fde68a" },
  { name: "Grafito", layout: "badge", bg: "#1f2937", accent: "#10b981", accent2: "#34d399", text: "#ffffff", sub: "#cbd5e1", muted: "#94a3b8", onAccent: "#052e16" },
];

export function normLevel(lv) {
  const s = String(lv || "").trim();
  if (!s) return "";
  return s.split(/\s+/).map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(" ");
}

export function resolveText(text, person, schoolName) {
  const lvl = person?.level || "";
  const bt = (person?.blood_type || "").trim();
  let out = String(text || "")
    .replaceAll("{{nombre}}", person?.full_name || "Nombre Apellido")
    .replaceAll("{{grado}}", person?.grade || "—")
    .replaceAll("{{uid}}", person?.nfc_uid || person?.id?.slice(-6) || "—")
    .replaceAll("{{colegio}}", schoolName || "Colegio")
    .replaceAll("{{nivel}}", lvl)
    .replaceAll("{{sangre}}", bt ? `Sangre: ${bt}` : "");
  out = out.replace(/\s*·\s*$/, "").replace(/·\s*·/g, "·");
  return out;
}

// Garantiza que la credencial de un ALUMNO muestre su nivel y tipo de sangre:
// inyecta {{nivel}} en la línea del grado y {{sangre}} en la línea del UID si no existen.
// Idempotente (no duplica). resolveText limpia los separadores colgantes si están vacíos.
export function withLevel(template, isTeacher = false) {
  if (!template || isTeacher) return template;
  const t = { ...template, front: { ...template.front }, back: { ...template.back } };
  ["front", "back"].forEach((side) => {
    const face = t[side];
    if (!face || !Array.isArray(face.elements)) return;
    let els = face.elements;
    let changed = false;
    if (!els.some((e) => e.type === "text" && /\{\{nivel\}\}/.test(e.text || ""))) {
      const grIdx = els.findIndex((e) => e.type === "text" && /\{\{grado\}\}/.test(e.text || ""));
      if (grIdx >= 0) {
        els = [...els];
        els[grIdx] = { ...els[grIdx], text: els[grIdx].text.replace(/\{\{grado\}\}/, "{{grado}} · {{nivel}}") };
        changed = true;
      }
    }
    if (!els.some((e) => e.type === "text" && /\{\{sangre\}\}/.test(e.text || ""))) {
      const uidIdx = els.findIndex((e) => e.type === "text" && /\{\{uid\}\}/.test(e.text || ""));
      if (uidIdx >= 0) {
        els = changed ? els : [...els];
        els[uidIdx] = { ...els[uidIdx], text: `${els[uidIdx].text} · {{sangre}}` };
        changed = true;
      }
    }
    if (changed) t[side] = { ...face, elements: els };
  });
  return t;
}

// Construye los elementos del FRENTE según el layout de cada plantilla.
function buildFront(theme, subtitle, isTeacher, logoUrl, o) {
  const t = theme;
  const port = o === "portrait";
  const T = (text, x, y, w, size, align, color, font = "Poppins", bold = false) => ({ id: uid(), type: "text", text, x, y, w, size, color, font, bold, align });
  const school = (x, y, w, size, align, color) => T("{{colegio}}", x, y, w, size, align, color, "Montserrat", true);
  const sub = (x, y, w, size, align, color) => T(subtitle, x, y, w, size, align, color, "Montserrat", false);
  const nm = (x, y, w, size, align, color) => T("{{nombre}}", x, y, w, size, align, color, "Poppins", true);
  const gr = (x, y, w, size, align, color) => T(isTeacher ? "{{grado}}" : "Grado: {{grado}} · {{nivel}}", x, y, w, size, align, color, "Poppins", false);
  const idf = (x, y, w, size, align, color) => T(isTeacher ? "ID: {{uid}}" : "ID: {{uid}} · {{sangre}}", x, y, w, size, align, color, "Space Mono", false);
  const ph = (x, y, w, h) => ({ id: uid(), type: "photo", x, y, w, h });
  const qr = (x, y, size) => ({ id: uid(), type: "qr", x, y, size });
  const lg = (x, y, w) => (logoUrl ? [{ id: uid(), type: "image", url: logoUrl, x, y, w }] : []);
  const rc = (x, y, w, h, color, extra = {}) => ({ id: uid(), type: "rect", x, y, w, h, color, radius: 0, ...extra });

  switch (t.layout) {
    case "sidebar":
      return port ? [
        rc(0, 0, 8, 100, t.accent),
        ...lg(40, 4, 22), school(8, 15, 84, 30, "center", t.text), sub(8, 23, 84, 16, "center", t.sub),
        ph(29, 30, 42, 29), nm(6, 63, 88, 34, "center", t.text), gr(6, 72, 88, 19, "center", t.sub),
        idf(6, 79, 88, 14, "center", t.muted), qr(40, 84, 20),
      ] : [
        rc(0, 0, 34, 100, t.accent), ...lg(5, 7, 24), ph(4, 36, 26, 44),
        school(37, 12, 58, 40, "left", t.text), sub(37, 25, 58, 20, "left", t.sub),
        nm(37, 46, 58, 42, "left", t.text), gr(37, 62, 58, 24, "left", t.sub),
        idf(37, 74, 40, 18, "left", t.muted), qr(77, 60, 20),
      ];
    case "topband":
      return port ? [
        rc(0, 0, 100, 22, t.accent), ...lg(82, 4, 14),
        school(6, 4, 72, 26, "left", t.onAccent), sub(6, 13, 60, 14, "left", t.onAccent),
        ph(29, 28, 42, 30), nm(6, 61, 88, 32, "center", t.text), gr(6, 70, 88, 18, "center", t.sub),
        idf(6, 77, 88, 14, "center", t.muted), qr(40, 83, 18),
      ] : [
        rc(0, 0, 100, 27, t.accent), ...lg(82, 5, 14),
        school(6, 5, 72, 32, "left", t.onAccent), sub(6, 17, 60, 15, "left", t.onAccent),
        ph(6, 36, 24, 52), nm(35, 40, 60, 42, "left", t.text), gr(35, 57, 60, 24, "left", t.sub),
        idf(35, 69, 45, 18, "left", t.muted), qr(76, 60, 20),
      ];
    case "diagonal":
      return port ? [
        rc(-10, -10, 130, 18, t.accent, { rot: -10 }), rc(-10, -3, 130, 5, t.accent2, { rot: -10 }),
        ...lg(40, 3, 20), school(6, 14, 88, 30, "center", t.text), sub(6, 22, 88, 16, "center", t.accent),
        ph(29, 29, 42, 30), nm(6, 62, 88, 32, "center", t.text), gr(6, 71, 88, 18, "center", t.sub),
        idf(6, 78, 88, 14, "center", t.muted), qr(40, 84, 18),
      ] : [
        rc(-10, -12, 130, 20, t.accent, { rot: -9 }), rc(-10, -4, 130, 6, t.accent2, { rot: -9 }),
        ...lg(78, 6, 16), school(7, 3, 60, 34, "left", t.onAccent), sub(7, 26, 50, 16, "left", t.accent),
        ph(7, 40, 24, 50), nm(35, 42, 60, 42, "left", t.text), gr(35, 59, 60, 24, "left", t.sub),
        idf(35, 71, 45, 18, "left", t.muted), qr(76, 60, 20),
      ];
    case "bottomband":
      return port ? [
        rc(0, 88, 100, 12, t.accent), ...lg(40, 4, 20),
        school(6, 15, 88, 30, "center", t.text), sub(6, 23, 88, 16, "center", t.sub),
        ph(29, 30, 42, 28), nm(6, 60, 88, 30, "center", t.text), gr(6, 69, 88, 17, "center", t.sub),
        idf(6, 76, 88, 13, "center", t.muted), qr(41, 82, 14),
        T("PERSONAL E INTRANSFERIBLE", 6, 91, 88, 13, "center", t.text, "Montserrat", true),
      ] : [
        rc(0, 84, 100, 16, t.accent), ...lg(80, 6, 16),
        school(7, 8, 60, 36, "left", t.text), sub(7, 22, 55, 18, "left", t.sub),
        ph(7, 34, 24, 44), nm(36, 38, 60, 42, "left", t.text), gr(36, 55, 60, 24, "left", t.sub),
        idf(36, 67, 45, 18, "left", t.muted), qr(74, 50, 20),
        T("PERSONAL E INTRANSFERIBLE", 6, 88, 88, 16, "center", t.text, "Montserrat", true),
      ];
    case "frame":
      return port ? [
        rc(0, 0, 100, 3, t.accent), rc(0, 97, 100, 3, t.accent), rc(0, 0, 4, 100, t.accent), rc(96, 0, 4, 100, t.accent),
        ...lg(40, 5, 20), school(8, 14, 84, 28, "center", t.text), sub(8, 22, 84, 15, "center", t.sub),
        ph(30, 29, 40, 28), nm(8, 62, 84, 30, "center", t.text), gr(8, 71, 84, 17, "center", t.sub),
        idf(8, 78, 84, 13, "center", t.muted), qr(41, 84, 14),
      ] : [
        rc(0, 0, 100, 4, t.accent), rc(0, 96, 100, 4, t.accent), rc(0, 0, 2.5, 100, t.accent), rc(97.5, 0, 2.5, 100, t.accent),
        ...lg(78, 8, 16), school(8, 10, 58, 38, "left", t.text), sub(8, 23, 58, 18, "left", t.sub),
        ph(8, 36, 24, 46), nm(37, 42, 56, 40, "left", t.text), gr(37, 58, 56, 24, "left", t.sub),
        idf(37, 70, 42, 18, "left", t.muted), qr(76, 58, 18),
      ];
    case "split":
      return port ? [
        rc(0, 0, 100, 30, t.accent), ...lg(40, 3, 20),
        school(6, 9, 88, 26, "center", t.onAccent), sub(6, 18, 88, 14, "center", t.onAccent),
        ph(30, 34, 40, 26), nm(6, 63, 88, 30, "center", t.text), gr(6, 72, 88, 17, "center", t.sub),
        idf(6, 79, 88, 13, "center", t.muted), qr(41, 84, 14),
      ] : [
        rc(0, 0, 42, 100, t.accent), ...lg(6, 8, 26), ph(8, 40, 26, 46),
        school(4, 74, 34, 18, "center", t.onAccent),
        sub(46, 26, 50, 18, "left", t.accent), nm(46, 40, 50, 42, "left", t.text),
        gr(46, 57, 50, 24, "left", t.sub), idf(46, 69, 45, 18, "left", t.muted), qr(76, 60, 20),
      ];
    case "corners":
      return port ? [
        rc(0, 0, 20, 12, t.accent), rc(80, 88, 20, 12, t.accent),
        ...lg(40, 5, 20), school(8, 16, 84, 30, "center", t.text), sub(8, 24, 84, 16, "center", t.sub),
        ph(29, 31, 42, 29), nm(6, 63, 88, 32, "center", t.text), gr(6, 72, 88, 18, "center", t.sub),
        idf(6, 79, 88, 14, "center", t.muted), qr(41, 84, 14),
      ] : [
        rc(0, 0, 14, 24, t.accent), rc(86, 76, 14, 24, t.accent),
        ...lg(78, 7, 16), school(8, 9, 60, 38, "left", t.text), sub(8, 23, 58, 18, "left", t.sub),
        ph(8, 36, 24, 48), nm(37, 42, 56, 42, "left", t.text), gr(37, 59, 56, 24, "left", t.sub),
        idf(37, 71, 42, 18, "left", t.muted), qr(76, 58, 18),
      ];
    case "centered":
      return port ? [
        ...lg(43, 4, 14), school(6, 13, 88, 30, "center", t.text), sub(6, 21, 88, 15, "center", t.sub),
        ph(30, 28, 40, 28), nm(6, 62, 88, 32, "center", t.text), rc(30, 72, 40, 1.2, t.accent),
        gr(6, 74, 88, 17, "center", t.sub), idf(6, 81, 88, 13, "center", t.muted), qr(43, 86, 12),
      ] : [
        ...lg(44, 5, 12), ph(6, 34, 22, 44),
        school(30, 14, 64, 38, "center", t.text), sub(30, 28, 64, 18, "center", t.sub),
        nm(30, 44, 64, 42, "center", t.text), rc(42, 60, 40, 1.5, t.accent),
        gr(30, 64, 64, 22, "center", t.sub), idf(30, 75, 64, 16, "center", t.muted), qr(78, 56, 18),
      ];
    case "photohero":
      return port ? [
        rc(0, 0, 100, 48, t.accent), ph(22, 5, 56, 40),
        school(6, 52, 88, 24, "center", t.text), sub(6, 61, 88, 14, "center", t.sub),
        nm(6, 70, 88, 28, "center", t.text), gr(6, 79, 88, 16, "center", t.sub),
        idf(6, 86, 62, 13, "left", t.muted), qr(72, 85, 14),
      ] : [
        rc(0, 0, 40, 100, t.accent), ph(4, 8, 32, 84), ...lg(80, 6, 16),
        school(44, 10, 52, 36, "left", t.text), sub(44, 23, 52, 18, "left", t.sub),
        nm(44, 44, 52, 40, "left", t.text), gr(44, 60, 52, 24, "left", t.sub),
        idf(44, 72, 42, 18, "left", t.muted), qr(78, 58, 18),
      ];
    case "badge":
    default:
      return port ? [
        rc(15, -5, 70, 16, t.accent, { radius: 10 }), school(15, 2, 70, 24, "center", t.onAccent),
        sub(6, 20, 88, 15, "center", t.sub), ph(29, 28, 42, 30), nm(6, 62, 88, 32, "center", t.text),
        gr(6, 71, 88, 18, "center", t.sub), idf(6, 78, 88, 14, "center", t.muted), qr(40, 84, 18),
      ] : [
        rc(20, -6, 60, 20, t.accent, { radius: 12 }), school(20, 3, 60, 28, "center", t.onAccent),
        sub(8, 24, 84, 18, "center", t.sub), ph(8, 34, 24, 50), nm(36, 40, 58, 42, "left", t.text),
        gr(36, 57, 58, 24, "left", t.sub), idf(36, 69, 45, 18, "left", t.muted),
        ...lg(80, 60, 14), qr(74, 50, 20),
      ];
  }
}

function buildBack(theme, o) {
  const t = theme;
  const T = (text, x, y, w, size, align, color, font = "Montserrat", bold = false) => ({ id: uid(), type: "text", text, x, y, w, size, color, font, bold, align });
  const rc = (x, y, w, h, color) => ({ id: uid(), type: "rect", x, y, w, h, color, radius: 0 });
  return o === "portrait" ? [
    rc(0, 0, 100, 5, t.accent),
    T("Esta credencial es personal e intransferible.", 6, 26, 88, 22, "center", t.text, "Montserrat", true),
    T("En caso de extravío, favor de reportarla a la institución.", 6, 40, 88, 16, "center", t.sub),
    T("TIPO DE SANGRE: ______\nCONTACTO: ______________", 6, 58, 88, 15, "center", t.muted, "Space Mono"),
    T("{{colegio}}", 6, 86, 88, 15, "center", t.muted),
  ] : [
    rc(0, 0, 100, 6, t.accent),
    T("Esta credencial es personal e intransferible.", 8, 22, 84, 24, "center", t.text, "Montserrat", true),
    T("En caso de extravío, favor de reportarla a la institución.", 8, 36, 84, 20, "center", t.sub),
    T("TIPO DE SANGRE: ______    CONTACTO: ______________", 8, 58, 84, 20, "left", t.muted, "Space Mono"),
    T("{{colegio}}", 8, 82, 84, 20, "center", t.muted),
  ];
}

function buildTemplate(theme, kind, schoolName, logoUrl, orientation = "landscape") {
  const isTeacher = kind === "teacher";
  const subtitle = isTeacher ? "DOCENTE" : "CREDENCIAL DE ALUMNO";
  return {
    front: { bg: theme.bg, bgImage: null, elements: buildFront(theme, subtitle, isTeacher, logoUrl, orientation) },
    back: { bg: theme.bg, bgImage: null, elements: buildBack(theme, orientation) },
    orientation,
  };
}

export function defaultTemplate(kind, schoolName, logoUrl) {
  return buildTemplate(THEMES[0], kind, schoolName, logoUrl, "landscape");
}

export async function toDataUrl(url) {
  try {
    const rel = url.startsWith("http") ? url : url;
    const res = await api.get(rel, { responseType: "blob" });
    return await new Promise((resolve) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.onerror = () => resolve(null);
      r.readAsDataURL(res.data);
    });
  } catch { return null; }
}

export async function makeQr(text) {
  try { return await QRCode.toDataURL(text || "UAICARD", { margin: 0, width: 300 }); } catch { return null; }
}

async function buildFontEmbedCss(families) {
  const uniq = [...new Set((families || []).filter(Boolean))];
  let css = "";
  for (const fam of uniq) {
    try {
      const cssUrl = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(fam)}:wght@400;600;700;900&display=swap`;
      let text = await fetch(cssUrl).then((r) => r.text());
      const urls = [...text.matchAll(/url\((https:\/\/[^)]+)\)/g)].map((m) => m[1]);
      for (const u of urls) {
        try {
          const blob = await fetch(u).then((r) => r.blob());
          const dataUrl = await new Promise((rs) => { const fr = new FileReader(); fr.onload = () => rs(fr.result); fr.readAsDataURL(blob); });
          text = text.split(u).join(dataUrl);
        } catch { /* ignore font file */ }
      }
      css += text + "\n";
    } catch { /* ignore family */ }
  }
  return css;
}

// Renderiza una cara de la credencial. `width` en px controla la escala.
export function CardFace({ face, person, schoolName, width = CARD_W, orientation = "landscape", imgMap = {}, editable = false, selectedId = null, onSelect, onDrag, onDelete, faceRef }) {
  const ratio = ratioFor(orientation);
  const height = Math.round(width / ratio);
  const fs = width / CARD_W;
  const cardRef = useRef(null);
  const bgImg = face.bgImage ? (imgMap[face.bgImage] || `${API}${face.bgImage}`) : null;

  const startDrag = (e, el) => {
    if (!editable) return;
    e.stopPropagation();
    e.preventDefault();
    onSelect && onSelect(el.id);
    const rect = cardRef.current.getBoundingClientRect();
    const startX = e.clientX, startY = e.clientY, ox = el.x, oy = el.y;
    const target = e.currentTarget;
    try { target.setPointerCapture(e.pointerId); } catch { /* ignore */ }
    const move = (ev) => {
      const dx = ((ev.clientX - startX) / rect.width) * 100;
      const dy = ((ev.clientY - startY) / rect.height) * 100;
      onDrag && onDrag(el.id, { x: Math.max(0, Math.min(99, ox + dx)), y: Math.max(0, Math.min(99, oy + dy)) });
    };
    const up = () => {
      try { target.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
      target.removeEventListener("pointermove", move);
      target.removeEventListener("pointerup", up);
      target.removeEventListener("pointercancel", up);
    };
    target.addEventListener("pointermove", move);
    target.addEventListener("pointerup", up);
    target.addEventListener("pointercancel", up);
  };

  return (
    <div
      ref={(n) => { cardRef.current = n; if (faceRef) faceRef.current = n; }}
      onClick={(e) => { if (editable && e.target === e.currentTarget) onSelect && onSelect(null); }}
      style={{
        width, height, position: "relative", overflow: "hidden", borderRadius: 16 * fs,
        background: face.bg || "#0b1f3a",
        backgroundImage: bgImg ? `url(${bgImg})` : "none", backgroundSize: "cover", backgroundPosition: "center",
        boxShadow: editable ? "0 10px 40px rgba(0,0,0,.35)" : undefined,
      }}
    >
      {face.elements.map((el) => {
        const selected = editable && selectedId === el.id;
        const common = {
          position: "absolute", left: `${el.x}%`, top: `${el.y}%`,
          cursor: editable ? "move" : "default",
          touchAction: editable ? "none" : "auto",
          outline: selected ? "2px solid #38bdf8" : "none", outlineOffset: 2,
        };
        if (el.type === "rect") {
          return <div key={el.id} onPointerDown={(e) => startDrag(e, el)}
            style={{ ...common, width: `${el.w}%`, height: `${el.h}%`, background: el.color, borderRadius: (el.radius || 0) * fs,
              transform: el.rot ? `rotate(${el.rot}deg)` : undefined, transformOrigin: "center" }} />;
        }
        if (el.type === "text") {
          if (el.font) loadFont(el.font);
          return (
            <div key={el.id} onPointerDown={(e) => startDrag(e, el)}
              style={{ ...common, width: `${el.w}%`, color: el.color, fontFamily: `'${el.font}', sans-serif`,
                fontSize: (el.size || 28) * fs, fontWeight: el.bold ? 800 : 500, textAlign: el.align || "left",
                lineHeight: 1.15, whiteSpace: "pre-wrap", userSelect: "none" }}>
              {resolveText(el.text, person, schoolName)}
            </div>
          );
        }
        if (el.type === "image") {
          const src = imgMap[el.url] || `${API}${el.url}`;
          return <img key={el.id} src={src} alt="" crossOrigin="anonymous" onPointerDown={(e) => startDrag(e, el)}
            style={{ ...common, width: `${el.w}%`, height: "auto", objectFit: "contain" }} draggable={false} />;
        }
        if (el.type === "photo") {
          const src = imgMap.__photo || (person?.photo_path ? `${API}/files/${person.photo_path}` : null);
          return (
            <div key={el.id} onPointerDown={(e) => startDrag(e, el)}
              style={{ ...common, width: `${el.w}%`, height: `${el.h}%`, borderRadius: 8 * fs, overflow: "hidden",
                background: "#e2e8f0", display: "flex", alignItems: "center", justifyContent: "center" }}>
              {src ? <img src={src} alt="" crossOrigin="anonymous" style={{ width: "100%", height: "100%", objectFit: "cover" }} draggable={false} />
                : <User style={{ width: "40%", height: "40%", color: "#94a3b8" }} />}
            </div>
          );
        }
        if (el.type === "qr") {
          const src = imgMap.__qr || null;
          const s = (el.size / 100) * width;
          return (
            <div key={el.id} onPointerDown={(e) => startDrag(e, el)}
              style={{ ...common, width: s, height: s, background: "#fff", borderRadius: 6 * fs, padding: 4 * fs }}>
              {src ? <img src={src} alt="QR" style={{ width: "100%", height: "100%" }} draggable={false} />
                : <QrCode style={{ width: "100%", height: "100%", color: "#0b1f3a" }} />}
            </div>
          );
        }
        return null;
      })}
      {editable && onDelete && selectedId && (() => {
        const el = face.elements.find((e) => e.id === selectedId);
        if (!el) return null;
        const wpc = el.w ?? el.size ?? 8;
        return (
          <button
            data-testid="cred-floating-delete"
            onPointerDown={(e) => { e.stopPropagation(); e.preventDefault(); }}
            onClick={(e) => { e.stopPropagation(); onDelete(el.id); }}
            title="Eliminar elemento"
            style={{
              position: "absolute", left: `${el.x + wpc}%`, top: `${el.y}%`,
              transform: "translate(-50%, -120%)", zIndex: 30,
              width: 30, height: 30, borderRadius: 9999, border: "2px solid #fff",
              background: "#dc2626", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
              boxShadow: "0 3px 10px rgba(0,0,0,.45)", cursor: "pointer", touchAction: "none",
            }}
          >
            <Trash2 style={{ width: 16, height: 16 }} />
          </button>
        );
      })()}
    </div>
  );
}

async function resolveImages(template, person, schoolName) {
  const map = {};
  const urls = new Set();
  ["front", "back"].forEach((s) => {
    (template[s].bgImage) && urls.add(template[s].bgImage);
    template[s].elements.forEach((el) => { if (el.type === "image" && el.url) urls.add(el.url); });
  });
  for (const u of urls) map[u] = await toDataUrl(u.startsWith("http") ? u : u);
  if (person?.photo_path) map.__photo = await toDataUrl(`/files/${person.photo_path}`);
  const qrText = `${window.location.origin}/tap/${person?.program_token || person?.id || ""}`;
  map.__qr = await makeQr(qrText);
  return map;
}

// ---- Recorte de imagen (logo / foto / fondo) ----
async function getCroppedBlob(src, area) {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.round(area.width));
      canvas.height = Math.max(1, Math.round(area.height));
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, area.x, area.y, area.width, area.height, 0, 0, area.width, area.height);
      canvas.toBlob((b) => resolve(b), "image/png", 0.95);
    };
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

const CROP_ASPECTS = [
  { label: "Libre", value: 0 },
  { label: "1:1", value: 1 },
  { label: "4:3", value: 4 / 3 },
  { label: "16:9", value: 16 / 9 },
  { label: "3:4", value: 3 / 4 },
];

function CropModal({ src, onCancel, onDone }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [aspect, setAspect] = useState(1);
  const [area, setArea] = useState(null);
  const [busy, setBusy] = useState(false);
  const onComplete = useCallback((_c, a) => setArea(a), []);

  const confirm = async () => {
    if (!area) return;
    setBusy(true);
    const blob = await getCroppedBlob(src, area);
    setBusy(false);
    if (blob) onDone(blob); else toast.error("No se pudo recortar la imagen");
  };

  return (
    <div className="fixed inset-0 z-[90] bg-black/80 flex items-center justify-center p-4" data-testid="cred-crop-modal">
      <div className="bg-white rounded-xl w-full max-w-lg overflow-hidden shadow-2xl">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-nardo-200">
          <Crop className="w-4 h-4 text-navy" />
          <h3 className="font-display font-bold text-navy text-sm flex-1">Recortar imagen</h3>
          <button onClick={onCancel} data-testid="cred-crop-cancel" className="p-1.5 text-nardo-500 hover:text-navy"><X className="w-4 h-4" /></button>
        </div>
        <div className="relative w-full h-72 bg-slate-900">
          <Cropper image={src} crop={crop} zoom={zoom} aspect={aspect || undefined}
            restrictPosition={false} onCropChange={setCrop} onZoomChange={setZoom} onCropComplete={onComplete} />
        </div>
        <div className="px-4 py-3 space-y-3">
          <div className="flex flex-wrap gap-1.5">
            {CROP_ASPECTS.map((a) => (
              <button key={a.label} onClick={() => setAspect(a.value)} data-testid={`cred-crop-aspect-${a.label}`}
                className={`text-xs px-2.5 py-1 rounded-md border ${aspect === a.value ? "bg-navy text-white border-navy" : "border-nardo-200 text-nardo-600 hover:border-navy"}`}>{a.label}</button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <ZoomIn className="w-4 h-4 text-nardo-500 shrink-0" />
            <input type="range" min="1" max="3" step="0.01" value={zoom} onChange={(e) => setZoom(+e.target.value)} data-testid="cred-crop-zoom" className="w-full" />
          </div>
          <div className="flex items-center justify-end gap-2">
            <button onClick={onCancel} className="text-xs border border-nardo-300 text-nardo-600 hover:text-navy px-3 py-2 rounded-md">Cancelar</button>
            <button onClick={confirm} disabled={busy} data-testid="cred-crop-confirm" className="inline-flex items-center gap-1.5 text-xs bg-navy hover:bg-navy-light text-white px-4 py-2 rounded-md font-semibold disabled:opacity-50">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Aplicar recorte
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Fila de muestras de color + selector nativo ("todos los colores").
function ColorField({ value, onChange, testid }) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-2">
        <input type="color" value={value || "#000000"} onChange={(e) => onChange(e.target.value)} data-testid={testid}
          className="w-10 h-9 rounded border border-nardo-200 cursor-pointer" />
        <input value={value || ""} onChange={(e) => onChange(e.target.value)}
          className="flex-1 min-w-0 border border-nardo-200 rounded-md px-2 py-1.5 text-xs font-mono outline-none focus:border-navy" />
      </div>
      <div className="flex flex-wrap gap-1">
        {PALETTE.map((c) => (
          <button key={c} type="button" onClick={() => onChange(c)} title={c} data-testid={`${testid}-swatch-${c}`}
            style={{ background: c }}
            className={`w-6 h-6 rounded border ${(value || "").toLowerCase() === c ? "ring-2 ring-offset-1 ring-navy border-navy" : "border-nardo-200"}`} />
        ))}
      </div>
    </div>
  );
}

export default function CredentialDesigner({ kind, schoolName, logoUrl, samplePerson, levels = [], initialLevel = "", onClose }) {
  const [tpl, setTpl] = useState(null);
  const [side, setSide] = useState("front");
  const [selId, setSelId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [fontQuery, setFontQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const fileRef = useRef(null);
  const bgFileRef = useRef(null);
  const canvasRef = useRef(null);
  const [cardW, setCardW] = useState(340);
  const [curTheme, setCurTheme] = useState(THEMES[0]);
  const [dirty, setDirty] = useState(false);
  const [cropState, setCropState] = useState(null); // { src, target }
  const [curLevel, setCurLevel] = useState(kind === "student" ? (initialLevel || "") : "");

  // Bloquea el scroll del fondo mientras el editor está abierto (evita que "se mueva atrás").
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      // Restaurar; si prev ya era 'hidden' (p.ej. por un remount de StrictMode),
      // limpiamos a "" para que la página quede scrolleable de nuevo.
      document.body.style.overflow = prev && prev !== "hidden" ? prev : "";
    };
  }, []);

  const orientation = tpl?.orientation || "landscape";

  useEffect(() => {
    const el = canvasRef.current;
    if (!el) return;
    const update = () => { const w = el.clientWidth - 24; setCardW(Math.max(240, Math.min(520, w))); };
    update();
    const ro = new ResizeObserver(update); ro.observe(el);
    return () => ro.disconnect();
  }, [loading]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/direccion/credential-template", { params: { kind, level: curLevel } });
        let t = data.template || defaultTemplate(kind, schoolName, logoUrl);
        if (!t.orientation) t.orientation = "landscape";
        t = withLevel(t, kind === "teacher");
        setTpl(t); setDirty(false); setSelId(null);
      } catch { setTpl(defaultTemplate(kind, schoolName, logoUrl)); }
      finally { setLoading(false); }
    })();
  }, [kind, curLevel]); // eslint-disable-line

  const face = tpl?.[side];
  const sel = face?.elements.find((e) => e.id === selId) || null;

  const patchFace = (updater) => { setDirty(true); setTpl((t) => ({ ...t, [side]: updater(t[side]) })); };
  const patchEl = (id, patch) => patchFace((f) => ({ ...f, elements: f.elements.map((e) => e.id === id ? { ...e, ...patch } : e) }));
  const addEl = (el) => { const withId = { id: uid(), ...el }; patchFace((f) => ({ ...f, elements: [...f.elements, withId] })); setSelId(withId.id); };
  const delEl = (id) => { patchFace((f) => ({ ...f, elements: f.elements.filter((e) => e.id !== id) })); setSelId(null); };

  const setOrientation = (o) => {
    if (o === orientation) return;
    if (dirty) {
      // Conserva TODAS las personalizaciones (colores, textos, posiciones); solo cambia la orientación.
      setTpl((t) => ({ ...t, orientation: o }));
      toast.success(o === "portrait" ? "Vertical · se mantuvieron tus cambios" : "Horizontal · se mantuvieron tus cambios");
    } else {
      setTpl(buildTemplate(curTheme, kind, schoolName, logoUrl, o));
      toast.success(o === "portrait" ? "Diseño vertical aplicado" : "Diseño horizontal aplicado");
    }
    setSelId(null);
  };
  const applyTheme = (theme) => {
    const built = buildTemplate(theme, kind, schoolName, logoUrl, orientation);
    setCurTheme(theme);
    setTpl(built); setSelId(null); setDirty(false);
    toast.success(`Plantilla "${theme.name}" aplicada`);
  };

  const uploadBlob = async (blob, target) => {
    try {
      const fd = new FormData(); fd.append("file", new File([blob], "credencial.png", { type: "image/png" }));
      const { data } = await api.post("/direccion/credential-asset", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (target === "bg") patchFace((fc) => ({ ...fc, bgImage: data.url }));
      else addEl({ type: "image", url: data.url, x: 38, y: 30, w: 26 });
      toast.success("Imagen agregada");
    } catch { toast.error("No se pudo subir la imagen"); }
  };
  const onUpload = (e, target) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setCropState({ src: reader.result, target });
    reader.readAsDataURL(f);
  };

  const save = async () => {
    setSaving(true);
    try { await api.put("/direccion/credential-template", { kind, level: curLevel, template: tpl }); setDirty(false); toast.success(kind === "student" && curLevel ? `Plantilla de ${curLevel} guardada.` : "Plantilla guardada. Se usará en todas las credenciales."); }
    catch { toast.error("No se pudo guardar"); }
    finally { setSaving(false); }
  };
  const resetStock = () => { if (window.confirm("¿Restaurar el diseño original de fábrica?")) { setTpl(defaultTemplate(kind, schoolName, logoUrl)); setCurTheme(THEMES[0]); setSelId(null); setDirty(false); } };

  const fonts = CREDENTIAL_FONTS.filter((f) => !fontQuery || f.toLowerCase().includes(fontQuery.toLowerCase()));
  const previewW = orientation === "portrait" ? Math.min(cardW, 320) : cardW;
  const themePreviews = useMemo(() => THEMES.map((t) => buildTemplate(t, kind, schoolName, logoUrl, orientation)), [kind, schoolName, logoUrl, orientation]);

  if (loading || !tpl) {
    return <div className="fixed inset-0 z-50 bg-slate-900 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-white" /></div>;
  }

  return (
    <div className="fixed inset-0 z-[70] bg-slate-900 flex flex-col" data-testid="credential-designer">
      {/* Top bar */}
      <div className="bg-white border-b border-nardo-200 px-3 sm:px-6 py-2.5">
        <div className="flex items-center gap-2">
          <Layers className="w-5 h-5 text-navy shrink-0" />
          <h2 className="font-display font-bold text-navy text-sm sm:text-base truncate flex-1 min-w-0">Editar credencial · {kind === "teacher" ? "Docentes" : "Alumnos"}</h2>
          <button onClick={onClose} data-testid="cred-close" className="p-2 text-nardo-500 hover:text-navy shrink-0"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex items-center gap-2 mt-2 flex-wrap">
          <div className="inline-flex rounded-md border border-nardo-200 overflow-hidden text-xs">
            <button onClick={() => { setSide("front"); setSelId(null); }} data-testid="cred-side-front" className={`px-3 py-1.5 font-medium ${side === "front" ? "bg-navy text-white" : "text-nardo-600"}`}>Frontal</button>
            <button onClick={() => { setSide("back"); setSelId(null); }} data-testid="cred-side-back" className={`px-3 py-1.5 font-medium ${side === "back" ? "bg-navy text-white" : "text-nardo-600"}`}>Trasera</button>
          </div>
          <div className="inline-flex rounded-md border border-nardo-200 overflow-hidden text-xs" data-testid="cred-orientation-toggle">
            <button onClick={() => setOrientation("landscape")} data-testid="cred-orient-landscape" className={`px-3 py-1.5 font-medium inline-flex items-center gap-1 ${orientation === "landscape" ? "bg-navy text-white" : "text-nardo-600"}`}><RectangleHorizontal className="w-3.5 h-3.5" /> Horizontal</button>
            <button onClick={() => setOrientation("portrait")} data-testid="cred-orient-portrait" className={`px-3 py-1.5 font-medium inline-flex items-center gap-1 ${orientation === "portrait" ? "bg-navy text-white" : "text-nardo-600"}`}><RectangleVertical className="w-3.5 h-3.5" /> Vertical</button>
          </div>
          <div className="flex-1 min-w-0" />
          <span className="text-[11px] text-nardo-400 hidden sm:inline">{orientation === "portrait" ? "54 × 85.6 mm" : "85.6 × 54 mm"}</span>
          <button onClick={resetStock} data-testid="cred-reset" className="inline-flex items-center gap-1.5 text-xs border border-nardo-300 text-nardo-600 hover:text-navy px-3 py-2 rounded-md"><RotateCcw className="w-3.5 h-3.5" /> Stock</button>
          <button onClick={save} disabled={saving} data-testid="cred-save-template" className="inline-flex items-center gap-1.5 text-xs bg-navy hover:bg-navy-light text-white px-4 py-2 rounded-md font-semibold disabled:opacity-50">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar</button>
        </div>
        {kind === "student" && levels.length > 0 && (
          <div className="flex items-center gap-2 mt-2 flex-wrap" data-testid="cred-level-selector">
            <span className="text-[11px] uppercase tracking-wider text-nardo-500 inline-flex items-center gap-1"><LayoutTemplate className="w-3 h-3" /> Nivel:</span>
            <div className="inline-flex rounded-md border border-nardo-200 overflow-hidden text-xs flex-wrap">
              <button onClick={() => setCurLevel("")} data-testid="cred-level-general" className={`px-3 py-1.5 font-medium ${curLevel === "" ? "bg-navy text-white" : "text-nardo-600"}`}>General</button>
              {levels.map((lv) => (
                <button key={lv} onClick={() => setCurLevel(lv)} data-testid={`cred-level-${lv}`} className={`px-3 py-1.5 font-medium border-l border-nardo-200 ${curLevel === lv ? "bg-navy text-white" : "text-nardo-600"}`}>{lv}</button>
              ))}
            </div>
            <span className="text-[11px] text-nardo-400 hidden sm:inline">{curLevel ? `Editas la plantilla de ${curLevel}` : "Plantilla base (se usa si un nivel no tiene diseño propio)"}</span>
          </div>
        )}

      </div>

      <div className="flex-1 flex flex-col lg:flex-row min-h-0 overflow-y-auto lg:overflow-hidden">
        {/* Canvas */}
        <div ref={canvasRef} className="flex items-start justify-center p-4 sm:p-6 lg:flex-1 lg:overflow-auto">
          <div className="w-full flex flex-col items-center gap-3">
            <CardFace face={face} person={samplePerson} schoolName={schoolName} width={previewW} orientation={orientation}
              editable selectedId={selId} onSelect={setSelId} onDrag={(id, p) => patchEl(id, p)} onDelete={(id) => delEl(id)} />
            <p className="text-center text-[11px] text-white/60 px-2">Arrastra los elementos para moverlos · toca para editarlos</p>
          </div>
        </div>

        {/* Panel */}
        <div className="w-full lg:w-80 bg-white lg:border-l border-t lg:border-t-0 border-nardo-200 lg:overflow-y-auto p-4 space-y-4 shrink-0">
          {/* Plantillas */}
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-nardo-500 mb-2 flex items-center gap-1"><LayoutTemplate className="w-3 h-3" /> Plantillas (10) · toca para aplicar</div>
            <div className="grid grid-cols-3 gap-2" data-testid="cred-templates">
              {THEMES.map((t, i) => (
                <button key={t.name} onClick={() => applyTheme(t)} data-testid={`cred-template-${i}`} title={t.name}
                  className={`rounded-md overflow-hidden border hover:ring-2 hover:ring-navy transition ${curTheme.name === t.name ? "ring-2 ring-navy border-navy" : "border-nardo-200"}`}>
                  <div className="pointer-events-none w-full flex items-center justify-center bg-slate-800 p-1" style={{ aspectRatio: orientation === "portrait" ? "0.72" : "1.585" }}>
                    <CardFace face={themePreviews[i].front} person={samplePerson} schoolName={schoolName} width={orientation === "portrait" ? 56 : 88} orientation={orientation} />
                  </div>
                  <div className="text-[9px] text-center text-nardo-500 py-0.5 truncate">{t.name}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Add elements */}
          <div className="border-t border-nardo-100 pt-3">
            <div className="text-[10px] uppercase tracking-[0.2em] text-nardo-500 mb-2">Agregar</div>
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => addEl({ type: "text", text: "Texto nuevo", x: 30, y: 45, w: 40, size: 30, color: curTheme.text || (face.bg === "#ffffff" ? "#111827" : "#ffffff"), font: "Montserrat", bold: false, align: "left" })} data-testid="cred-add-text" className="flex items-center gap-2 text-xs border border-nardo-200 rounded-md px-3 py-2 hover:border-navy"><Type className="w-4 h-4" /> Texto</button>
              <button onClick={() => fileRef.current?.click()} data-testid="cred-add-image" className="flex items-center gap-2 text-xs border border-nardo-200 rounded-md px-3 py-2 hover:border-navy"><ImageIcon className="w-4 h-4" /> Logo/Img</button>
              <button onClick={() => addEl({ type: "qr", x: 74, y: 58, size: 22 })} data-testid="cred-add-qr" className="flex items-center gap-2 text-xs border border-nardo-200 rounded-md px-3 py-2 hover:border-navy"><QrCode className="w-4 h-4" /> QR</button>
              <button onClick={() => addEl({ type: "photo", x: 6, y: 32, w: 26, h: 52 })} data-testid="cred-add-photo" className="flex items-center gap-2 text-xs border border-nardo-200 rounded-md px-3 py-2 hover:border-navy"><User className="w-4 h-4" /> Foto</button>
              <button onClick={() => addEl({ type: "rect", x: 20, y: 40, w: 40, h: 12, color: "#3b62a8", radius: 8 })} data-testid="cred-add-rect" className="flex items-center gap-2 text-xs border border-nardo-200 rounded-md px-3 py-2 hover:border-navy col-span-2"><Square className="w-4 h-4" /> Forma / banda de color</button>
            </div>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => onUpload(e, "img")} data-testid="cred-image-input" />
          </div>

          {/* Background */}
          <div className="border-t border-nardo-100 pt-3">
            <div className="text-[10px] uppercase tracking-[0.2em] text-nardo-500 mb-2 flex items-center gap-1"><Palette className="w-3 h-3" /> Fondo de la {side === "front" ? "cara frontal" : "cara trasera"}</div>
            <ColorField value={face.bg} onChange={(c) => patchFace((f) => ({ ...f, bg: c }))} testid="cred-bg-color" />
            <div className="flex items-center gap-2 mt-2">
              <button onClick={() => bgFileRef.current?.click()} className="flex-1 text-xs border border-nardo-200 rounded-md px-3 py-2 hover:border-navy">Imagen de fondo</button>
              {face.bgImage && <button onClick={() => patchFace((f) => ({ ...f, bgImage: null }))} className="text-nardo-400 hover:text-red-500 p-2"><Trash2 className="w-4 h-4" /></button>}
              <input ref={bgFileRef} type="file" accept="image/*" className="hidden" onChange={(e) => onUpload(e, "bg")} />
            </div>
          </div>

          {/* Selected element editor */}
          {sel ? (
            <div className="border-t border-nardo-100 pt-3 space-y-3" data-testid="cred-element-editor">
              <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-[0.2em] text-nardo-500">Elemento · {sel.type}</div>
                <button onClick={() => delEl(sel.id)} data-testid="cred-delete-element" className="text-red-500 hover:text-red-600 text-xs inline-flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" /> Eliminar</button>
              </div>

              {sel.type === "text" && (
                <>
                  <textarea value={sel.text} onChange={(e) => patchEl(sel.id, { text: e.target.value })} rows={2} data-testid="cred-text-content" className="w-full border border-nardo-200 rounded-md p-2 text-sm outline-none focus:border-navy" />
                  <div className="flex flex-wrap gap-1">
                    {DYNAMIC_FIELDS.map((d) => <button key={d.token} onClick={() => patchEl(sel.id, { text: (sel.text || "") + " " + d.token })} className="text-[10px] bg-navy/10 text-navy rounded px-2 py-1" title={d.label}>{d.token}</button>)}
                  </div>
                  <details>
                    <summary className="text-[11px] text-nardo-500 cursor-pointer">Frases rápidas</summary>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {QUICK_TEXTS.map((t, i) => <button key={i} onClick={() => patchEl(sel.id, { text: t })} className="text-[10px] border border-nardo-200 rounded px-2 py-1 hover:border-navy text-left">{t.slice(0, 22)}</button>)}
                    </div>
                  </details>
                  <div>
                    <input value={fontQuery} onChange={(e) => setFontQuery(e.target.value)} placeholder="Buscar fuente… (+100)" data-testid="cred-font-search" className="w-full border border-nardo-200 rounded-md p-2 text-xs outline-none focus:border-navy mb-1" />
                    <select value={sel.font} onChange={(e) => { loadFont(e.target.value); patchEl(sel.id, { font: e.target.value }); }} size={5} data-testid="cred-font-select" className="w-full border border-nardo-200 rounded-md text-sm outline-none focus:border-navy">
                      {fonts.map((f) => <option key={f} value={f} style={{ fontFamily: f }}>{f}</option>)}
                    </select>
                  </div>
                  <ColorField value={sel.color} onChange={(c) => patchEl(sel.id, { color: c })} testid="cred-text-color" />
                  <div className="flex items-center gap-2">
                    <button onClick={() => patchEl(sel.id, { bold: !sel.bold })} className={`p-2 rounded border ${sel.bold ? "bg-navy text-white border-navy" : "border-nardo-200"}`}><Bold className="w-4 h-4" /></button>
                    {[["left", AlignLeft], ["center", AlignCenter], ["right", AlignRight]].map(([a, Ic]) => (
                      <button key={a} onClick={() => patchEl(sel.id, { align: a })} className={`p-2 rounded border ${sel.align === a ? "bg-navy text-white border-navy" : "border-nardo-200"}`}><Ic className="w-4 h-4" /></button>
                    ))}
                  </div>
                  <label className="text-[11px] text-nardo-500 block">Tamaño: {sel.size}px
                    <input type="range" min="10" max="160" value={sel.size} onChange={(e) => patchEl(sel.id, { size: +e.target.value })} data-testid="cred-text-size" className="w-full" />
                  </label>
                  <label className="text-[11px] text-nardo-500 block">Ancho: {sel.w}%
                    <input type="range" min="10" max="98" value={sel.w} onChange={(e) => patchEl(sel.id, { w: +e.target.value })} className="w-full" />
                  </label>
                </>
              )}
              {sel.type === "image" && (
                <label className="text-[11px] text-nardo-500 block">Tamaño (ancho): {sel.w}%
                  <input type="range" min="6" max="100" value={sel.w} onChange={(e) => patchEl(sel.id, { w: +e.target.value })} data-testid="cred-image-size" className="w-full" />
                </label>
              )}
              {sel.type === "photo" && (
                <>
                  <label className="text-[11px] text-nardo-500 block">Ancho: {sel.w}%<input type="range" min="10" max="80" value={sel.w} onChange={(e) => patchEl(sel.id, { w: +e.target.value })} className="w-full" /></label>
                  <label className="text-[11px] text-nardo-500 block">Alto: {sel.h}%<input type="range" min="10" max="94" value={sel.h} onChange={(e) => patchEl(sel.id, { h: +e.target.value })} className="w-full" /></label>
                </>
              )}
              {sel.type === "qr" && (
                <label className="text-[11px] text-nardo-500 block">Tamaño QR: {sel.size}%<input type="range" min="10" max="55" value={sel.size} onChange={(e) => patchEl(sel.id, { size: +e.target.value })} className="w-full" /></label>
              )}
              {sel.type === "rect" && (
                <>
                  <ColorField value={sel.color} onChange={(c) => patchEl(sel.id, { color: c })} testid="cred-rect-color" />
                  <label className="text-[11px] text-nardo-500 block">Ancho: {sel.w}%<input type="range" min="2" max="100" value={sel.w} onChange={(e) => patchEl(sel.id, { w: +e.target.value })} className="w-full" /></label>
                  <label className="text-[11px] text-nardo-500 block">Alto: {sel.h}%<input type="range" min="1" max="100" value={sel.h} onChange={(e) => patchEl(sel.id, { h: +e.target.value })} className="w-full" /></label>
                  <label className="text-[11px] text-nardo-500 block">Redondeo: {sel.radius || 0}<input type="range" min="0" max="40" value={sel.radius || 0} onChange={(e) => patchEl(sel.id, { radius: +e.target.value })} className="w-full" /></label>
                </>
              )}
            </div>
          ) : (
            <div className="border-t border-nardo-100 pt-3 text-[12px] text-nardo-400 flex items-center gap-1"><ChevronRight className="w-3 h-3" /> Selecciona un elemento en la tarjeta para editarlo.</div>
          )}
        </div>
      </div>

      {cropState && (
        <CropModal
          src={cropState.src}
          onCancel={() => setCropState(null)}
          onDone={(blob) => { const target = cropState.target; setCropState(null); uploadBlob(blob, target); }}
        />
      )}
    </div>
  );
}

// ---- Exportador reutilizable (PNG / PDF) usado por la lista de personas ----
export function useCredentialExport(schoolName, logoUrl) {
  const [exp, setExp] = useState(null);
  const frontRef = useRef(null), backRef = useRef(null);
  const [busy, setBusy] = useState(null);

  const run = useCallback(async (person, template, format) => {
    setBusy(person.id + format);
    const orientation = template.orientation || "landscape";
    try {
      const imgMap = await resolveImages(template, person, schoolName);
      const families = [...template.front.elements, ...template.back.elements].filter((e) => e.type === "text").map((e) => e.font);
      const fontEmbedCSS = await buildFontEmbedCss(families);
      setExp({ person, imgMap, template });
      await new Promise((r) => setTimeout(r, 350));
      const opts = { pixelRatio: 2, cacheBust: true, fontEmbedCSS };
      const safe = (person.full_name || "credencial").replace(/\s+/g, "_");
      if (format === "png") {
        const frontPng = await toPng(frontRef.current, opts);
        const backPng = await toPng(backRef.current, opts);
        const a = document.createElement("a"); a.href = frontPng; a.download = `credencial_${safe}_frente.png`; a.click();
        const b = document.createElement("a"); b.href = backPng; b.download = `credencial_${safe}_reverso.png`; b.click();
      } else {
        // PDF: JPEG comprimido para que el archivo pese poco y descargue sin problemas.
        const jopts = { ...opts, quality: 0.92, backgroundColor: "#ffffff" };
        const frontJpg = await toJpeg(frontRef.current, jopts);
        const backJpg = await toJpeg(backRef.current, jopts);
        const dims = orientation === "portrait" ? [54, 85.6] : [85.6, 54];
        const pdf = new jsPDF({ orientation: orientation === "portrait" ? "portrait" : "landscape", unit: "mm", format: dims, compress: true });
        pdf.addImage(frontJpg, "JPEG", 0, 0, dims[0], dims[1]);
        pdf.addPage(dims, orientation === "portrait" ? "portrait" : "landscape");
        pdf.addImage(backJpg, "JPEG", 0, 0, dims[0], dims[1]);
        pdf.save(`credencial_${safe}.pdf`);
      }
      toast.success("Credencial generada");
    } catch (e) { toast.error("No se pudo generar la credencial"); }
    finally { setBusy(null); setExp(null); }
  }, [schoolName]);

  const runBatch = useCallback(async (people, resolveTemplate, { includeBack = true, pageSize = "letter" } = {}) => {
    if (!people?.length) { toast.error("No hay alumnos en este grupo"); return; }
    setBusy("batch");
    try {
      const orientation = (resolveTemplate(people[0]).orientation) || "landscape";
      const cardMM = orientation === "portrait" ? { w: 54, h: 85.6 } : { w: 85.6, h: 54 };
      const page = pageSize === "a4" ? { w: 210, h: 297 } : { w: 215.9, h: 279.4 };
      const fmt = pageSize === "a4" ? "a4" : "letter";
      const margin = 10, gap = 4;
      const cols = Math.max(1, Math.floor((page.w - 2 * margin + gap) / (cardMM.w + gap)));
      const rows = Math.max(1, Math.floor((page.h - 2 * margin + gap) / (cardMM.h + gap)));
      const perPage = cols * rows;
      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: fmt, compress: true });

      const fronts = [], backs = [];
      for (let i = 0; i < people.length; i++) {
        const person = people[i];
        const template = resolveTemplate(person);
        if (!template.orientation) template.orientation = "landscape";
        const imgMap = await resolveImages(template, person, schoolName);
        const families = [...template.front.elements, ...template.back.elements].filter((e) => e.type === "text").map((e) => e.font);
        const fontEmbedCSS = await buildFontEmbedCss(families);
        setExp({ person, imgMap, template });
        await new Promise((r) => setTimeout(r, 280));
        // JPEG comprimido + resolución de impresión (~1.6x sobre 1012px ≈ 480 DPI en tarjeta de 85.6mm).
        // Mantiene nitidez para imprimir pero reduce el peso del PDF ~10-15x vs PNG.
        const opts = { pixelRatio: 1.6, quality: 0.9, cacheBust: true, fontEmbedCSS, backgroundColor: "#ffffff" };
        fronts.push(await toJpeg(frontRef.current, opts));
        if (includeBack) backs.push(await toJpeg(backRef.current, opts));
        setBusy(`batch ${i + 1}/${people.length}`);
      }
      setExp(null);

      const totalW = cols * cardMM.w + (cols - 1) * gap;
      const totalH = rows * cardMM.h + (rows - 1) * gap;
      const startX = (page.w - totalW) / 2;
      const startY = (page.h - totalH) / 2;
      let firstPageUsed = false;
      const placeSection = (imgs, mirror) => {
        for (let idx = 0; idx < imgs.length; idx++) {
          const cell = idx % perPage;
          if (cell === 0) { if (!firstPageUsed) firstPageUsed = true; else pdf.addPage(fmt, "portrait"); }
          const c0 = cell % cols, r = Math.floor(cell / cols);
          const c = mirror ? (cols - 1 - c0) : c0;
          const x = startX + c * (cardMM.w + gap);
          const y = startY + r * (cardMM.h + gap);
          pdf.setDrawColor(210); pdf.setLineWidth(0.1);
          pdf.rect(x, y, cardMM.w, cardMM.h);
          pdf.addImage(imgs[idx], "JPEG", x, y, cardMM.w, cardMM.h);
        }
      };
      placeSection(fronts, false);
      if (includeBack && backs.length) placeSection(backs, true);
      pdf.save("credenciales_lote.pdf");
      toast.success(`PDF con ${people.length} credencial(es) generado`);
    } catch (e) { toast.error("No se pudo generar el lote de credenciales"); }
    finally { setBusy(null); setExp(null); }
  }, [schoolName]);

  const node = exp ? (
    <div style={{ position: "fixed", left: -99999, top: 0 }} aria-hidden>
      <CardFace face={exp.template.front} person={exp.person} schoolName={schoolName} width={CARD_W} orientation={exp.template.orientation || "landscape"} imgMap={exp.imgMap} faceRef={frontRef} />
      <CardFace face={exp.template.back} person={exp.person} schoolName={schoolName} width={CARD_W} orientation={exp.template.orientation || "landscape"} imgMap={exp.imgMap} faceRef={backRef} />
    </div>
  ) : null;

  return { run, runBatch, node, busy };
}
